import { store } from "../../redux/storeConfig/store";
import {
  addAppointmentModal,
  addFollowupProgramModal,
  addGateModal,
  addPackageModal,
  addPrivateModal,
  addSaleModal,
  addServiceModal,
  addStaffModal,
  addTrainerModal,
  addUserModal,
  addWodModal,
  addWorkshopModal,
  setCourtModal,
  setRetailModal,
} from "../../redux/actions/sidebar/sidebarAction";
import { setOpenLeadModal } from "../../redux/actions/lead/leadAction";
import { setVoiceAdd } from "../../redux/reducers/voiceAdd/voiceAddReducer";
import { trackVoiceAddEvent } from "./trackSession";
import {
  buildMemberCreatePayload,
  validateMemberCreatePayload,
} from "../member/createPayload";
import { buildVoiceCatalog } from "./buildVoiceCatalog";
import { isIdentityDocumentError, problemsFromMap } from "./draftProblems";
import { isVoiceDraftEditing } from "./voiceDraftGuard";

export { isVoiceDraftEditing, skipVoiceCreate } from "./voiceDraftGuard";

const SKIP_KEYS = {
  smsGateway: true,
  superGymAdmin: true,
  packageDetails: true,
  id: true,
};

const MEMBER_META_KEYS = [
  "healthProblem",
  "handedness",
  "smokingHabits",
  "referralSource",
  "medicalHistory",
  "insuranceDetails",
  "referralNote",
];

const empty = (value) => {
  if (value === undefined || value === null || value === "") return true;
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

const shownValue = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => shownValue(item))
      .filter(Boolean)
      .join(", ");
  }
  if (value && typeof value === "object") {
    return value.name || value.label || value.title || "";
  }
  return String(value);
};

const usableValue = (value) => {
  if (value === undefined || typeof value === "function") return false;
  if (value && typeof value === "object") {
    if (typeof File !== "undefined" && value instanceof File) return false;
    if (typeof Blob !== "undefined" && value instanceof Blob) return false;
  }
  return true;
};

const lookupRecord = (value) => {
  if (!value || typeof value !== "object") return value;
  const id =
    value.id ||
    (value.value && typeof value.value === "object"
      ? value.value.id
      : value.value);
  const name = value.name || value.label || "";
  if (value.pending) return { pending: value.pending, name };
  if (id === undefined || id === null || id === "") {
    return name ? { name } : value;
  }
  return { id, name };
};

export const voiceReviewActionLabel = (fallback) =>
  isVoiceDraftEditing() ? "Save to review" : fallback;

const catalogForState = () => {
  const state = store.getState();
  const login = JSON.parse(
    localStorage.getItem("connectToFitGymPanelUser") || "{}"
  );
  const role =
    login.isSuperGymAdmin && login.isSubAdmin
      ? "STAFF"
      : login.role || "";
  return buildVoiceCatalog({
    gym: state.gym && state.gym.gym,
    gymConfig: state.gym && state.gym.gymConfig,
    role,
    loggedInRole: login.role,
    permissions: state.permissions && state.permissions.permissions,
    subAdminType: state.permissions && state.permissions.subAdminType,
    subAdminPermissions:
      state.permissions && state.permissions.subAdminPermissions,
    loginUser: state.login && state.login.loginUser,
  });
};

const cardIdFromState = () => {
  const membershipCard = store.getState().membershipCard || {};
  return membershipCard.RFIDCardId || "";
};

const adaptValues = (draft, values) => {
  const next = { ...(values || {}) };
  if (next.metaData && typeof next.metaData === "object") {
    MEMBER_META_KEYS.forEach((key) => {
      if (next.metaData[key] !== undefined) next[key] = next.metaData[key];
    });
    delete next.metaData;
  }
  if (draft.type === "member") {
    const rfid = String(
      next.RFIDCardIdField || next.cardMembershipId || cardIdFromState() || ""
    ).trim();
    if (rfid) {
      next.RFIDCardIdField = rfid;
      next.cardMembershipId = rfid;
      next.requireCard = true;
    }
  }
  if (draft.type === "sale" && next.user && !next.member) {
    next.member = lookupRecord(next.user);
  }
  if (draft.type === "sale") {
    if (Array.isArray(next.packages)) {
      next.packages = next.packages.map(lookupRecord);
    }
    if (Array.isArray(next.products)) {
      next.products = next.products.map(lookupRecord);
    }
  }
  if (draft.type === "lead") {
    if (next.assignedStaff) next.assignedStaff = lookupRecord(next.assignedStaff);
    if (next.referralSource) {
      next.referralSource = lookupRecord(next.referralSource);
    }
  }
  if (draft.type === "class" || draft.type === "workshop") {
    if (Array.isArray(next.timesArray)) {
      next.timesArray = next.timesArray.filter(
        (item) => item && item.from && item.to
      );
    }
    if (Array.isArray(next.assignedPackages)) {
      next.assignedPackages = next.assignedPackages.map(lookupRecord);
    }
    if (next.assignedPackages && next.assignedPackages.length) {
      next.autoCreateSinglePackage = false;
    }
  }
  return next;
};

const mergeFields = (draft, values) => {
  const fields = { ...(draft.fields || {}) };
  const incoming = adaptValues(draft, values);
  Object.keys(incoming).forEach((key) => {
    if (SKIP_KEYS[key] || !usableValue(incoming[key])) return;
    fields[key] = incoming[key];
  });
  return fields;
};

export const rebuildVoiceDraft = (draft, values, options) => {
  const fields = mergeFields(draft, values);
  const action = catalogForState().find((item) => item.type === draft.type);
  if (!action) {
    return {
      ...draft,
      fields,
      scans: [],
      fieldErrors: options && options.fromForm ? [] : draft.fieldErrors || [],
      needsForm: false,
      ready: true,
    };
  }
  const saved = [];
  const missing = [];
  const scans = [];
  action.fields.forEach((field) => {
    const value = fields[field.key];
    if (!empty(value) || field.assumed === "placeholder") {
      saved.push({
        field: field.label,
        value:
          field.assumed === "placeholder" && empty(value)
            ? "Default picture"
            : shownValue(value),
      });
      return;
    }
    if (field.input === "scan") {
      if (field.required) {
        missing.push(field.label);
        scans.push({ key: field.key, label: field.label });
      }
      return;
    }
    if (
      field.required &&
      field.input !== "form" &&
      field.assumed !== "placeholder"
    ) {
      missing.push(field.label);
    }
  });
  (action.groups || []).forEach((group) => {
    const filled = group.keys.some((key) => !empty(fields[key]));
    if (!filled) missing.push(group.label);
  });
  const fieldErrors = freshFieldErrors(draft, fields, options);
  return {
    ...draft,
    fields,
    saved,
    missing,
    scans,
    fieldErrors,
    ready: missing.length === 0 && fieldErrors.length === 0,
    needsForm: false,
  };
};

const memberErrorsFromFields = (fields) => {
  const gym = store.getState().gym && store.getState().gym.gym;
  const payload = buildMemberCreatePayload(fields, {
    country: gym && gym.country,
  });
  return problemsFromMap(validateMemberCreatePayload(payload));
};

const freshFieldErrors = (draft, fields, options) => {
  if (draft.type === "member") {
    const current = memberErrorsFromFields(fields);
    if (options && options.fromForm) return current;
    const stillInvalid = {};
    current.forEach((item) => {
      stillInvalid[item.field] = item;
    });
    return (draft.fieldErrors || []).filter(
      (item) => item && (!item.field || stillInvalid[item.field])
    );
  }
  if (draft.type === "sale") {
    const previous = ((draft.fieldErrors || []).concat(draft.problems || [])).filter(
      isIdentityDocumentError
    );
    if (previous.length && empty(fields.attachment)) return previous;
    return [];
  }
  return options && options.fromForm ? [] : draft.fieldErrors || [];
};

const replaceDraft = (draft, values, options) => {
  const voice = store.getState().voiceAdd || {};
  const updated = rebuildVoiceDraft(draft, values, options);
  const drafts = (voice.drafts || []).map((item) =>
    item.id === draft.id ? updated : item
  );
  store.dispatch(
    setVoiceAdd({
      drafts,
      readyForReview: drafts.length > 0 && drafts.every((item) => item.ready),
      scan: null,
      ...((options && options.returnToReview)
        ? {
            prefill: null,
            editingDraftId: null,
            phase: "ready",
            step: "review",
            open: true,
          }
        : {}),
    })
  );
  return updated;
};

export const closeVoiceEditForms = () => {
  const dispatch = store.dispatch;
  dispatch(addUserModal(false));
  dispatch(addPackageModal(false, false, {}));
  dispatch(addSaleModal(false, false, {}));
  dispatch(setRetailModal(false, false, {}));
  dispatch(addTrainerModal(false));
  dispatch(addAppointmentModal(false));
  dispatch(addStaffModal(false, false, {}));
  dispatch(addWodModal(false));
  dispatch(addFollowupProgramModal(false));
  dispatch(addGateModal(false));
  dispatch(addPrivateModal(false));
  dispatch(addWorkshopModal(false));
  dispatch(setCourtModal(false));
  dispatch(addServiceModal(false));
  dispatch(setOpenLeadModal(false));
};

export const saveVoiceDraftEdit = (values) => {
  const voice = store.getState().voiceAdd || {};
  const draft = voice.prefill;
  if (!draft) return false;
  if (!voice.editingDraftId && !voice.sessionId) return false;
  replaceDraft(draft, values, { returnToReview: true, fromForm: true });
  closeVoiceEditForms();
  trackVoiceAddEvent("edit_saved", {
    draftId: draft.id,
    type: draft.type,
    label: draft.label,
  });
  return true;
};

export const applyVoiceCardScan = (cardId, meta) => {
  const scanned = String(cardId || "").trim();
  if (!scanned) return false;
  const voice = store.getState().voiceAdd || {};
  const scan = (meta && meta.scan) || voice.scan || {};
  const draftId =
    (meta && meta.draftId) || scan.draftId || voice.editingDraftId;
  const draft = (voice.drafts || []).find((item) => item.id === draftId);
  if (!draft) return false;
  const key = (meta && meta.key) || scan.key || "RFIDCardIdField";
  replaceDraft(
    draft,
    {
      [key]: scanned,
      RFIDCardIdField: scanned,
      cardMembershipId: scanned,
      requireCard: true,
    },
    { returnToReview: !voice.editingDraftId }
  );
  return true;
};
